import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const NAV = [
  { to: "/", key: "home", testId: "nav-link-home" },
  { to: "/about", key: "about", testId: "nav-link-about" },
  { to: "/innovation", key: "innovation", testId: "nav-link-mouyia" },
  { to: "/applications", key: "applications", testId: "nav-link-applications" },
  { to: "/mobility", key: "mobility", testId: "nav-link-mobility" },
  { to: "/services", key: "services", testId: "nav-link-services" },
  { to: "/contact", key: "contact", testId: "nav-link-contact" },
];

function LanguageSwitcher() {
  const { lang, setLang, t } = useLanguage();
  return (
    <div
      className="flex items-center rounded-full border border-border bg-card p-0.5"
      role="group"
      aria-label={t.nav.langLabel}
      data-testid="lang-switcher-btn"
    >
      {["fr", "en"].map((l) => (
        <button
          key={l}
          type="button"
          data-testid={`lang-switcher-${l}`}
          onClick={() => setLang(l)}
          aria-pressed={lang === l}
          className={`px-3 py-1 rounded-full text-xs font-mono font-semibold uppercase tracking-wider transition-colors duration-200 ${
            lang === l ? "bg-ink text-white" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export function Header() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <>
      <a href="#main" className="skip-link">
        {t.nav.skip}
      </a>
      <header className="sticky top-0 z-50 border-b border-border/80 bg-background/90 backdrop-blur-xl">
        <div className="container-site flex h-16 sm:h-[4.5rem] items-center justify-between gap-4">
          <Link to="/" data-testid="nav-brand-logo" className="flex items-center gap-3 group" onClick={() => setOpen(false)}>
            <img src="/logo-512.png" alt="" className="h-11 w-11 shrink-0 object-contain" aria-hidden="true" />
            <span className="leading-tight">
              <span className="block font-display font-extrabold tracking-tight text-sm sm:text-base">CONGO BOURSE</span>
              <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                Innovation & Technology
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="Main">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={item.testId}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                    isActive ? "text-foreground bg-secondary" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                  }`
                }
              >
                {t.nav[item.key]}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link
              to="/applications/learnova"
              data-testid="nav-link-learnova"
              className="hidden xl:inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-ink px-4 py-2 text-sm font-semibold text-white transition-all duration-200 hover:bg-ochre hover:shadow-md active:scale-[0.98]"
            >
              {t.nav.cta}
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <button
              type="button"
              className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-md border border-border"
              aria-expanded={open}
              aria-label={open ? t.nav.menuClose : t.nav.menuOpen}
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.nav
              key={location.pathname}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="lg:hidden overflow-hidden border-t border-border bg-background"
              aria-label="Mobile"
            >
              <div className="container-site py-4 flex flex-col gap-1">
                {NAV.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/"}
                    data-testid={`mobile-${item.testId}`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `px-3 py-3 rounded-md text-base font-medium ${
                        isActive ? "bg-secondary text-foreground" : "text-muted-foreground"
                      }`
                    }
                  >
                    {t.nav[item.key]}
                  </NavLink>
                ))}
                <Link
                  to="/applications/learnova"
                  data-testid="mobile-nav-link-learnova"
                  onClick={() => setOpen(false)}
                  className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-ink px-4 py-3 text-sm font-semibold text-white"
                >
                  {t.nav.cta}
                  <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>
    </>
  );
}
